import ProductLocationsApiV2 from 'api/v2/admins/product-locations-api-v2'
import { InputField } from 'components'
import RedirectButton from 'components/atoms/redirect-button'
import TitlePage from 'components/atoms/title-page'
import { ALERT_TYPES } from 'constants/alert-constants'
import { LOCATION_TYPE_FORM_OPTIONS, LOCATION_TYPE_VALUES } from 'domains/bnk-warehouse-location/constants/location-type-labels'
import { Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { Alert, Button, Card, Spinner } from 'reactstrap'
import * as Yup from 'yup'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const schema = Yup.object().shape({
  name      : Yup.string().required('Required'),
  type_name : Yup.string().required('Required'),
  active    : Yup.boolean().required('Required')
})

const BnkWarehouseLocationEditPage = ({ pageUtils }) => {
  const { id } = useParams()
  const location = useLocation()
  const [ productLocation, setProductLocation ] = useState(null)
  const [ isLoading, setIsLoading ]             = useState(true)
  const [ loadError, setLoadError ]             = useState('')

  useEffect(() => {
    setIsLoading(true)
    setLoadError('')

    ProductLocationsApiV2.show(id)
      .then((response) => {
        const location = response?.data?.product_location || null

        setProductLocation(location)
        setIsLoading(false)
      })
      .catch((error) => {
        setProductLocation(null)
        setIsLoading(false)
        setLoadError('Unable to fetch location detail')
        pageUtils?.setApiErrorMsg?.(error?.response?.data || error.message)
        pageUtils?.setAlertMsg?.('Unable to fetch location detail', ALERT_TYPES.DANGER)
      })
  }, [ id, pageUtils ])

  const initialValues = useMemo(() => ({
    name      : productLocation?.name || '',
    type_name : productLocation?.type_name || LOCATION_TYPE_VALUES.ITEM,
    active    : productLocation?.active !== undefined ? productLocation.active : true
  }), [ productLocation ])

  const handleSubmit = async (values, formikBag) => {
    await ProductLocationsApiV2.update({
      id        : id,
      name      : values.name,
      type_name : values.type_name,
      active    : values.active
    })
      .then((response) => {
        const location = response?.data?.product_location || null

        if (location) {
          setProductLocation(location)
        }

        pageUtils?.setAlertMsg?.(`Location ${location?.name || values.name} has been updated.`, ALERT_TYPES.SUCCESS)

        formikBag.resetForm({
          values: {
            name      : location?.name || values.name,
            type_name : location?.type_name || values.type_name,
            active    : location?.active !== undefined ? location.active : values.active
          }
        })
      })
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error?.response?.data || error.message)
        formikBag.setErrors(error?.response?.data?.messages || {})
      })
  }

  const requestedReturnTo = new URLSearchParams(location.search).get('return_to')
  const backPath = requestedReturnTo?.startsWith('/app/warehouse-team/location/list-page')
    ? requestedReturnTo
    : '/app/warehouse-team/location/list-page'

  return (
    <>
      <TitlePage mainTitle={ 'Location Master' } subTitle={ 'Edit' } />

      <RedirectButton
        color='secondary'
        text='Back to Location Master'
        path={ backPath }
        className='mb-3'
      />

      {
        isLoading
          ? (
            <Card body className='text-center'>
              <Spinner color='primary' />
            </Card>
          )
          : (
            <Card body>
              {
                loadError && (
                  <Alert color='warning'>
                    { loadError }
                  </Alert>
                )
              }

              <Formik
                initialValues={ initialValues }
                enableReinitialize={ true }
                validationSchema={ schema }
                onSubmit={ handleSubmit }
              >
                {
                  (formikBag) => (
                    <Form>
                      <InputField
                        type='text'
                        label='name'
                        name='name'
                        required={ true }
                      />

                      <InputField
                        type='select'
                        label='type name'
                        name='type_name'
                        required={ true }
                      >
                        {
                          LOCATION_TYPE_FORM_OPTIONS.map((option) => (
                            <option key={ option.key } value={ option.key }>{ option.label }</option>
                          ))
                        }
                      </InputField>

                      <InputField
                        type='select'
                        label='active'
                        name='active'
                        required={ true }
                      >
                        <option value={ true }>{ 'Active' }</option>
                        <option value={ false }>{ 'Inactive' }</option>
                      </InputField>

                      <Button
                        color='primary'
                        disabled={ formikBag.isSubmitting }
                        type='submit'
                      >
                        {
                          formikBag.isSubmitting
                            ? <i className='ni ni-settings-gear-65 spin' />
                            : 'Update Location'
                        }
                      </Button>
                    </Form>
                  )
                }
              </Formik>
            </Card>
          )
      }
    </>
  )
}

BnkWarehouseLocationEditPage.propTypes = propTypes

export default BnkWarehouseLocationEditPage
